<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from "vue";
import { useGlobalStore } from "@stores/global.js";
import { useShopifyStore } from "@stores/shopify.js";
import { Swiper, SwiperSlide } from "swiper/vue";
import Button from "@components/global/Button.vue";
import "swiper/css";

const global = useGlobalStore();
const shopify = useShopifyStore();

const slider = ref({});
const muted = ref(true);
const activeIndex = ref(0);

// Mobile-optimised assets are used below the `lg` breakpoint, falling back
// per-field to the desktop asset when a mobile variant isn't set.
const desktopQuery = window.matchMedia("(min-width: 1024px)");
const isDesktop = ref(desktopQuery.matches);

const slideVideo = (slide) =>
	(!isDesktop.value && slide.video_mobile) || slide.video;

const slideImage = (slide) =>
	(!isDesktop.value && slide.image_mobile) || slide.image;

const slideImageAlt = (slide) =>
	(!isDesktop.value && slide.image_mobile
		? slide.image_mobile_alt
		: slide.image_alt) || slide.title;

const slidePoster = (slide) =>
	(!isDesktop.value && (slide.video_mobile_poster || slide.image_mobile)) ||
	slide.video_poster ||
	slide.image ||
	undefined;

const IMAGE_SLIDE_DURATION = 10000;
const VIDEO_FALLBACK_DURATION = 10000;

let autoAdvanceTimer = null;

const clearAutoAdvance = () => {
	if (autoAdvanceTimer) {
		clearTimeout(autoAdvanceTimer);
		autoAdvanceTimer = null;
	}
};

const goToNextSlide = () => {
	if (slides.value.length <= 1) return;
	if (slider.value?.slideNext) slider.value.slideNext();
};

const scheduleAutoAdvance = (delay) => {
	clearAutoAdvance();
	if (slides.value.length <= 1) return;
	autoAdvanceTimer = setTimeout(goToNextSlide, delay);
};

const onVideoEnded = () => goToNextSlide();

const getActiveVideo = () => {
	const swiper = slider.value;
	const activeSlide = swiper?.slides?.[swiper.activeIndex];
	return activeSlide?.querySelector("video") ?? null;
};

const startPlayback = (video) => {
	const playback = video.play();
	if (playback?.then) {
		playback
			.then(clearAutoAdvance)
			.catch(() => scheduleAutoAdvance(VIDEO_FALLBACK_DURATION));
	}
};

// iOS treats `preload` on an off-screen <video> as `none`, so by the time a
// later slide becomes active its video often has no buffered data and a plain
// `play()` is rejected. If it isn't ready yet, nudge it to load and play once
// it has data.
const playVideo = (video) => {
	if (!video) return;
	video.muted = muted.value;

	if (video.readyState >= 2) {
		startPlayback(video);
	} else {
		video.addEventListener("loadeddata", () => startPlayback(video), {
			once: true,
		});
		if (video.preload !== "auto") video.preload = "auto";
		video.load();
	}
};

// Pre-buffer the next slide's video so it's ready to autoplay the moment we
// arrive (Swiper tags every slide, including loop clones, with its real index).
const primeUpcomingVideo = (swiper) => {
	const total = slides.value.length;
	if (total <= 1) return;

	const nextRealIndex = (swiper.realIndex + 1) % total;
	const nextSlide = swiper.slides.find(
		(el) =>
			Number(el.getAttribute("data-swiper-slide-index")) === nextRealIndex,
	);
	const video = nextSlide?.querySelector("video");
	if (video && video.readyState < 2) {
		if (video.preload !== "auto") video.preload = "auto";
		video.load();
	}
};

const syncVideos = (swiper) => {
	if (!swiper?.slides) return;

	clearAutoAdvance();
	let activeHasVideo = false;

	swiper.slides.forEach((slideEl, index) => {
		const video = slideEl.querySelector("video");
		if (!video) return;

		if (index === swiper.activeIndex) {
			activeHasVideo = true;
			video.addEventListener("ended", onVideoEnded);
			playVideo(video);
		} else {
			video.pause();
			video.muted = true;
			video.currentTime = 0;
		}
	});

	if (!activeHasVideo) {
		scheduleAutoAdvance(IMAGE_SLIDE_DURATION);
	}

	primeUpcomingVideo(swiper);
};

const onSwiper = (swiper) => {
	slider.value = swiper;
	syncVideos(swiper);
};

const onSlideChange = (swiper) => {
	activeIndex.value = swiper.realIndex;
	syncVideos(swiper);
};

const goToSlide = (index) => {
	if (slider.value?.slideToLoop) {
		slider.value.slideToLoop(index);
	}
};

const toggleMute = () => {
	muted.value = !muted.value;
	playVideo(getActiveVideo());
};

const onFirstGesture = () => {
	const video = getActiveVideo();
	if (video?.paused) playVideo(video);
};

// Carry the previous asset's scrub position over to the freshly-swapped
// source once its duration is known. Clamped just short of the end so a
// shorter cut still plays its final moments and fires `ended` naturally.
const restoreVideoPosition = (video, time) => {
	if (!time) return;
	const seek = () => {
		if (Number.isFinite(video.duration)) {
			video.currentTime = Math.min(
				time,
				Math.max(video.duration - 0.25, 0),
			);
		}
	};
	if (video.readyState >= 1) {
		seek();
	} else {
		video.addEventListener("loadedmetadata", seek, { once: true });
	}
};

const onViewportChange = async (event) => {
	const activeSlide = slides.value[activeIndex.value];
	const beforeSrc = activeSlide ? slideVideo(activeSlide)?.[0] : undefined;
	const resumeTime = getActiveVideo()?.currentTime ?? 0;

	isDesktop.value = event.matches;

	const afterSrc = activeSlide ? slideVideo(activeSlide)?.[0] : undefined;
	if (beforeSrc === afterSrc) return;

	await nextTick();

	const video = getActiveVideo();
	if (video && beforeSrc && afterSrc) {
		restoreVideoPosition(video, resumeTime);
	}
	syncVideos(slider.value);
};

const gestureEvents = ["touchstart", "pointerdown", "keydown"];

onMounted(() => {
	desktopQuery.addEventListener("change", onViewportChange);
	gestureEvents.forEach((event) =>
		window.addEventListener(event, onFirstGesture, { passive: true }),
	);
});

onBeforeUnmount(() => {
	clearAutoAdvance();
	desktopQuery.removeEventListener("change", onViewportChange);
	gestureEvents.forEach((event) =>
		window.removeEventListener(event, onFirstGesture),
	);
});

const slides = computed(() => shopify.homepageHeroSlides);
const hasSlides = computed(() => slides.value.length > 0);
</script>

<template>
	<section v-if="hasSlides">
		<Swiper
			@swiper="onSwiper"
			@slideChange="onSlideChange"
			:loop="true"
			class="size-full"
		>
			<SwiperSlide v-for="(slide, index) in slides" :key="index">
				<div
					class="group text-text-inverted relative isolate flex h-[calc(100svh-var(--header-height)-var(--announcement-bar-height))] w-full items-end justify-center overflow-hidden p-16 before:absolute before:inset-0 before:-z-1 before:size-full before:bg-linear-to-t before:from-black/50 before:to-transparent md:p-24 lg:p-48"
					@mouseenter="
						if (global.pointerFine) {
							global.setHeaderTheme(
								slide.collection?.theme?.background ||
									slide.product?.theme?.background,
								slide.collection?.theme?.accent ||
									slide.product?.theme?.accent,
								slide.collection?.theme?.surface ||
									slide.product?.theme?.surface,
							);
							$event.currentTarget.style.setProperty(
								'--card-accent',
								slide.collection?.theme?.accent ||
									slide.product?.theme?.accent,
							);
							$event.currentTarget.style.setProperty(
								'--card-background',
								slide.collection?.theme?.background ||
									slide.product?.theme?.background,
							);
						}
					"
					@mouseleave="
						global.setHeaderTheme(null, null, null);
						$event.currentTarget.style.removeProperty('--card-accent');
						$event.currentTarget.style.removeProperty(
							'--card-background',
						);
					"
				>
					<video
						v-if="slideVideo(slide)"
						:src="slideVideo(slide)[0]"
						:poster="slidePoster(slide)"
						playsinline
						muted
						:loop="slides.length <= 1"
						preload="auto"
						class="absolute inset-0 -z-2 size-full object-cover transition-transform duration-500 group-hover:scale-105"
					/>
					<button
						v-if="slideVideo(slide)"
						@click="toggleMute"
						class="absolute right-16 bottom-16 z-40 flex size-40 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm transition-colors hover:bg-black/60 md:right-24 md:bottom-24"
						:aria-label="muted ? 'Unmute' : 'Mute'"
					>
						<svg
							v-if="muted"
							xmlns="http://www.w3.org/2000/svg"
							width="20"
							height="20"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						>
							<path d="M11 5 6 9H2v6h4l5 4zM22 9l-6 6M16 9l6 6" />
						</svg>
						<svg
							v-else
							xmlns="http://www.w3.org/2000/svg"
							width="20"
							height="20"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						>
							<path d="M11 5 6 9H2v6h4l5 4z" />
							<path
								d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"
							/>
						</svg>
					</button>
					<img
						v-else-if="slideImage(slide)"
						:src="slideImage(slide)"
						:alt="slideImageAlt(slide)"
						class="absolute inset-0 -z-2 size-full object-cover transition-transform duration-500 group-hover:scale-105"
					/>
					<div class="flex flex-col items-center gap-32 text-center">
						<div class="flex flex-col items-center gap-24">
							<div class="flex flex-col items-center gap-8">
								<component
									:is="index === 0 ? 'h1' : 'h2'"
									class="h1 font-primary-condensed"
								>
									{{ slide.title }}
								</component>
								<p
									class="body-md max-w-416 text-pretty"
									v-if="slide.description"
								>
									{{ slide.description }}
								</p>
							</div>
							<Button
								v-if="slide.collection"
								:to="{
									name: 'collection',
									params: { slug: slide.collection.slug },
								}"
								class="box-link -card-theme"
							>
								Shop now
							</Button>
							<Button
								v-if="slide.product"
								:to="{
									name: 'product',
									params: { slug: slide.product.slug },
								}"
								class="box-link -card-theme"
							>
								Shop now
							</Button>
						</div>
						<div
							v-if="slides.length > 1"
							class="z-20 flex items-center gap-8"
						>
							<button
								v-for="(_, dotIndex) in slides"
								:key="dotIndex"
								@click="goToSlide(dotIndex)"
								class="size-4 rounded-full transition-colors duration-300 hover:bg-white"
								:class="
									activeIndex === dotIndex ? 'bg-white' : 'bg-white/40'
								"
								:aria-label="`Go to slide ${dotIndex + 1}`"
							/>
						</div>
					</div>
				</div>
			</SwiperSlide>
		</Swiper>
	</section>
</template>
